import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { MONTH_ORDER, type StaffHeadcountRow } from './constants';
import type { UseStaffHeadcountResult } from '../../hooks/useStaffHeadcount';
import type { UseHeadcountPlanResult } from '../../hooks/useHeadcountPlan';

// 雇用形態別 内訳の表示順と識別色 (社員→契約→パート→派遣→… の順で並べる)
const EMPLOYMENT_ORDER = ['社員', '契約', 'パート', '派遣', '役員', '外注', '出向', '未設定'];
const EMPLOYMENT_COLORS: Record<string, string> = {
  '社員': '#3b82f6',
  '契約': '#06b6d4',
  'パート': '#10b981',
  '派遣': '#f59e0b',
  '役員': '#8b5cf6',
  '外注': '#ec4899',
  '出向': '#64748b',
  '未設定': '#cbd5e1',
};
const employmentColor = (name: string) => EMPLOYMENT_COLORS[name] || '#94a3b8';

type Props = {
  staff: UseStaffHeadcountResult;
  fiscalYear: number;
  currentMonth: number;
  selectedBranchCodes: string[];
  plan: UseHeadcountPlanResult;
};

/**
 * 従業員数 推移・増減セクション（経営概況ページ専用）。
 * 当月サマリーカード（全社）・12ヶ月推移グラフ・月次在籍数増減テーブル（部署別ドリルダウン）・
 * 採用計画の予実対比（計画在籍/計画採用/差異）を描画する。
 */
export default function StaffHeadcountSection({ staff, fiscalYear, currentMonth, selectedBranchCodes, plan }: Props) {
  const {
    staffRows,
    staffLoading,
    expandedStaffBranches,
    toggleStaffBranch,
    expandedHeadcountDepts,
    toggleHeadcountDepts,
  } = staff;

  const displayYear = currentMonth >= 9 ? fiscalYear - 1 : fiscalYear;

  // 行集合に対する指標の合算 (在籍数は支店/部門で排他のため単純合算で全社計になる)
  // 拠点絞り込み時はサーバー側で既に対象拠点に限定された行集合が返るため、ここでは単純合算する
  const sumMetric = (arr: StaffHeadcountRow[], key: string) =>
    arr.reduce((s, r) => s + (Number(r[key]) || 0), 0);

  // 全社・通期の集計 (入退社は12ヶ月合算、在籍数は期末=8月末時点のストック値)
  const companyYearHires = MONTH_ORDER.reduce((s, m) => s + sumMetric(staffRows, `month_${m}_hires`), 0);
  const companyYearLeaves = MONTH_ORDER.reduce((s, m) => s + sumMetric(staffRows, `month_${m}_leaves`), 0);
  const companyYearNet = companyYearHires - companyYearLeaves;
  const periodEndHeadcount = sumMetric(staffRows, 'month_8_headcount');
  const periodEndPrev = sumMetric(staffRows, 'month_8_headcount_prev');
  const periodEndYoy = periodEndPrev !== 0 ? (periodEndHeadcount / periodEndPrev) * 100 : null;

  // 採用計画 予実対比。計画在籍は期首在籍 (9月実績 − 9月純増減) を起点に計画採用を累積した計画線。
  // 差異 = 実績 − 計画 (在籍・採用とも)。退職は計画に含めないため、計画在籍は採用のみで増える。
  const { planHiresByMonth, planVersionName, hasPlan } = plan;
  const periodStartHeadcount =
    sumMetric(staffRows, 'month_9_headcount') -
    (sumMetric(staffRows, 'month_9_hires') - sumMetric(staffRows, 'month_9_leaves'));
  const planComparison = (() => {
    let cumHires = 0;
    return MONTH_ORDER.map((m) => {
      const planHires = planHiresByMonth[m] || 0;
      cumHires += planHires;
      const plannedHeadcount = periodStartHeadcount + cumHires;
      const actualHeadcount = sumMetric(staffRows, `month_${m}_headcount`);
      const actualHires = sumMetric(staffRows, `month_${m}_hires`);
      return {
        m,
        planHires,
        plannedHeadcount,
        headcountDiff: actualHeadcount - plannedHeadcount,
        hiresDiff: actualHires - planHires,
      };
    });
  })();

  // 予実テーブルに追加する行の定義 (計画在籍・在籍差異・計画採用・採用差異)
  const diffColor = (v: number) => (v > 0 ? '#059669' : v < 0 ? '#ef4444' : '#94a3b8');
  const signed = (v: number) => (v === 0 ? '0' : `${v > 0 ? '+' : ''}${v}`);
  const planRowDefs: {
    key: string;
    label: string;
    bold?: boolean;
    format: (p: typeof planComparison[number]) => string;
    color: (p: typeof planComparison[number]) => string;
  }[] = [
    { key: 'plannedHeadcount', label: '計画在籍', bold: true, format: (p) => p.plannedHeadcount.toLocaleString(), color: () => '#4338ca' },
    { key: 'headcountDiff', label: '在籍 差異（実績−計画）', format: (p) => signed(p.headcountDiff), color: (p) => diffColor(p.headcountDiff) },
    { key: 'planHires', label: '計画採用（+）', format: (p) => (p.planHires > 0 ? `+${p.planHires}` : '0'), color: (p) => (p.planHires > 0 ? '#4338ca' : '#cbd5e1') },
    { key: 'hiresDiff', label: '採用 差異（実績−計画）', format: (p) => signed(p.hiresDiff), color: (p) => diffColor(p.hiresDiff) },
  ];

  // 指定した行集合について 12ヶ月分の指標系列を算出
  const buildSeries = (arr: StaffHeadcountRow[]) =>
    MONTH_ORDER.map(m => {
      const headcount = sumMetric(arr, `month_${m}_headcount`);
      const hires = sumMetric(arr, `month_${m}_hires`);
      const leaves = sumMetric(arr, `month_${m}_leaves`);
      const prev = sumMetric(arr, `month_${m}_headcount_prev`);
      return {
        m,
        headcount,
        hires,
        leaves,
        net: hires - leaves,
        prev,
        yoy: prev !== 0 ? (headcount / prev) * 100 : null as number | null,
      };
    });

  // 指標ごとのセル整形 (値・文字色)
  const metricRowDefs: {
    key: 'headcount' | 'hires' | 'leaves' | 'net' | 'yoy';
    label: string;
    format: (s: ReturnType<typeof buildSeries>[number]) => string;
    color: (s: ReturnType<typeof buildSeries>[number]) => string;
    bold?: boolean;
  }[] = [
    {
      key: 'headcount', label: '在籍数', bold: true,
      format: s => s.headcount.toLocaleString(),
      color: () => '#1e293b',
    },
    {
      key: 'hires', label: '入社',
      format: s => (s.hires > 0 ? `+${s.hires}` : '0'),
      color: s => (s.hires > 0 ? '#059669' : '#cbd5e1'),
    },
    {
      key: 'leaves', label: '退職',
      format: s => (s.leaves > 0 ? `-${s.leaves}` : '0'),
      color: s => (s.leaves > 0 ? '#ef4444' : '#cbd5e1'),
    },
    {
      key: 'net', label: '純増減',
      format: s => `${s.net >= 0 ? '+' : ''}${s.net}`,
      color: s => (s.net > 0 ? '#059669' : s.net < 0 ? '#ef4444' : '#94a3b8'),
    },
    {
      key: 'yoy', label: '前年比',
      format: s => (s.yoy === null ? '-' : `${s.yoy.toFixed(1)}%`),
      color: s => (s.yoy === null ? '#94a3b8' : s.yoy >= 100 ? '#059669' : '#ef4444'),
    },
  ];

  // 1エンティティ (全社/支店) の指標ブロック (バナー行 + 5指標行) を描画
  // hideBanner=true でバナー行を抑止し、指標行のみ描画する (テーブルタイトルと重複させたくないケース用)
  const renderEntityBlock = (
    label: string,
    arr: StaffHeadcountRow[],
    opts: { isCompany?: boolean; branchCode?: string; expandable?: boolean; hideBanner?: boolean } = {}
  ) => {
    const series = buildSeries(arr);
    const isExpanded = opts.branchCode ? !!expandedStaffBranches[opts.branchCode] : false;

    // 在籍数行の部署別ドリルダウン用。行集合を department でグループ化し、部署ごとの12ヶ月系列を算出
    const deptGroups = (() => {
      const map = new Map<string, { name: string; rows: StaffHeadcountRow[] }>();
      arr.forEach(r => {
        const code = r.department || '';
        const name = r.department_name || '(部署未設定)';
        if (!map.has(code)) map.set(code, { name, rows: [] });
        map.get(code)!.rows.push(r);
      });
      return Array.from(map.entries())
        .map(([code, v]) => ({ code, name: v.name, series: buildSeries(v.rows) }))
        // 指定年度内に在籍数(残数)または入退社(動き)が発生した部署のみ表示する
        .filter(g => g.series.some(s => s.headcount > 0 || s.hires > 0 || s.leaves > 0))
        .sort((a, b) => a.code.localeCompare(b.code));
    })();
    const deptsExpanded = !!expandedHeadcountDepts[label];
    const bannerBg = opts.isCompany ? '#eff6ff' : '#f8fafc';
    const bannerColor = opts.isCompany ? '#1e3a8a' : '#334155';
    const borderBottom = opts.isCompany ? '2px solid #bfdbfe' : '1px solid #e2e8f0';
    return (
      <React.Fragment key={label}>
        {/* バナー行 (エンティティ名)。名称セルは左列固定、月セルは空で帯色のみ */}
        {!opts.hideBanner && (
          <TableRow
            onClick={opts.expandable && opts.branchCode ? () => toggleStaffBranch(opts.branchCode!) : undefined}
            sx={{ backgroundColor: bannerBg, cursor: opts.expandable ? 'pointer' : 'default', '&:hover': opts.expandable ? { backgroundColor: '#eef2f7' } : undefined }}
          >
            <TableCell sx={{ fontWeight: 800, color: bannerColor, position: 'sticky', left: 0, zIndex: 9, backgroundColor: bannerBg, borderBottom, py: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {opts.expandable ? (
                  <IconButton size="small" sx={{ mr: 0.5, p: 0.25, color: '#94a3b8' }}>
                    {isExpanded ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 22, mr: 0.5 }} />
                )}
                {label}
              </Box>
            </TableCell>
            {MONTH_ORDER.map(m => (
              <TableCell key={m} sx={{ backgroundColor: m === currentMonth ? '#dbeafe' : bannerBg, borderBottom, py: 0.75 }} />
            ))}
          </TableRow>
        )}

        {/* 5指標行。在籍数行はクリックで部署別ドリルダウンを開閉する */}
        {metricRowDefs.map(def => {
          const isHeadcount = def.key === 'headcount';
          const headcountExpandable = isHeadcount && deptGroups.length > 0;
          return (
            <React.Fragment key={`${label}-${def.key}`}>
              <TableRow
                onClick={headcountExpandable ? () => toggleHeadcountDepts(label) : undefined}
                sx={{
                  backgroundColor: '#ffffff',
                  cursor: headcountExpandable ? 'pointer' : 'default',
                  '&:hover': headcountExpandable ? { backgroundColor: '#f8fafc' } : undefined,
                }}
              >
                <TableCell sx={{ fontSize: '13px', color: '#64748b', pl: 5, position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#ffffff', borderBottom: '1px dashed #f1f5f9' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {headcountExpandable ? (
                      <IconButton size="small" sx={{ mr: 0.5, p: 0.25, color: '#94a3b8' }}>
                        {deptsExpanded ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 22, mr: 0.5 }} />
                    )}
                    {def.label}
                  </Box>
                </TableCell>
                {series.map(s => (
                  <TableCell
                    key={s.m}
                    align="right"
                    sx={{
                      fontSize: '13px',
                      fontWeight: def.bold ? 700 : 500,
                      color: def.color(s),
                      backgroundColor: s.m === currentMonth ? '#eff6ff' : 'transparent',
                      borderBottom: '1px dashed #f1f5f9',
                    }}
                  >
                    {def.format(s)}
                  </TableCell>
                ))}
              </TableRow>

              {/* 在籍数の部署別内訳 (各月末時点の在籍数のみ) */}
              {isHeadcount && deptsExpanded && deptGroups.map(g => (
                <TableRow key={`${label}-dept-${g.code}`} sx={{ backgroundColor: '#fbfdff' }}>
                  <TableCell sx={{ fontSize: '12px', color: '#94a3b8', pl: 9, position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#fbfdff', borderBottom: '1px dashed #f1f5f9' }}>
                    {g.name}
                  </TableCell>
                  {g.series.map(s => (
                    <TableCell
                      key={s.m}
                      align="right"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#475569',
                        backgroundColor: s.m === currentMonth ? '#eff6ff' : 'transparent',
                        borderBottom: '1px dashed #f1f5f9',
                      }}
                    >
                      {s.headcount.toLocaleString()}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  // 全社合計の12ヶ月系列 (グラフ用、月ラベル付与)
  const staffChartData = buildSeries(staffRows).map(s => ({ ...s, month: `${s.m}月` }));

  // 推移グラフ用ツールチップ (在籍数・前年・入退社・純増減)
  const StaffChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0].payload;
    const lineRow = (name: string, value: string, color: string) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color }}>{name}</Typography>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color }}>{value}</Typography>
      </Box>
    );
    return (
      <Box sx={{ backgroundColor: '#334155', color: '#fff', borderRadius: '8px', px: 1.5, py: 1, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.2)', minWidth: '150px' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>{label}</Typography>
        {lineRow('在籍数', `${d.headcount.toLocaleString()}名`, '#fff')}
        {lineRow('前年在籍', d.prev !== 0 ? `${d.prev.toLocaleString()}名` : '-', '#cbd5e1')}
        {lineRow('入社', `+${d.hires}名`, '#4ade80')}
        {lineRow('退職', `-${d.leaves}名`, '#f87171')}
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #475569' }}>
          {lineRow('純増減', `${d.net >= 0 ? '+' : ''}${d.net}名`, d.net >= 0 ? '#4ade80' : '#f87171')}
          {d.yoy !== null && lineRow('前年比', `${d.yoy.toFixed(1)}%`, d.yoy >= 100 ? '#4ade80' : '#f87171')}
        </Box>
      </Box>
    );
  };

  // サマリーカード (全社・当月)
  const summaryCards = [
    { title: '期末在籍数', value: periodEndHeadcount, unit: '名', icon: <Icons.Groups sx={{ fontSize: '18px' }} />, color: '#eff6ff', iconColor: '#3b82f6' },
    { title: '通期入社', value: companyYearHires, unit: '名', icon: <Icons.PersonAdd sx={{ fontSize: '18px' }} />, color: '#ecfdf5', iconColor: '#059669' },
    { title: '通期退職', value: companyYearLeaves, unit: '名', icon: <Icons.PersonRemove sx={{ fontSize: '18px' }} />, color: '#fef2f2', iconColor: '#ef4444' },
    { title: '通期純増減', value: companyYearNet, unit: '名', icon: <Icons.SwapHoriz sx={{ fontSize: '18px' }} />, color: '#f5f3ff', iconColor: '#8b5cf6', signed: true },
  ];

  // 雇用形態別 内訳 (期末在籍=8月末時点 / 通期入社・退職=12ヶ月合算 / 純増減)。
  // 在籍・入退社いずれも0の雇用形態は表示しない。表示順は EMPLOYMENT_ORDER 準拠 (未定義は末尾)。
  const employmentBreakdown = (() => {
    const map = new Map<string, StaffHeadcountRow[]>();
    staffRows.forEach(r => {
      const key = r.employment_status || '未設定';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries())
      .map(([name, rows]) => {
        const periodEnd = sumMetric(rows, 'month_8_headcount');
        const hires = MONTH_ORDER.reduce((s, m) => s + sumMetric(rows, `month_${m}_hires`), 0);
        const leaves = MONTH_ORDER.reduce((s, m) => s + sumMetric(rows, `month_${m}_leaves`), 0);
        return { name, periodEnd, hires, leaves, net: hires - leaves };
      })
      .filter(e => e.periodEnd > 0 || e.hires > 0 || e.leaves > 0)
      .sort((a, b) => {
        const ia = EMPLOYMENT_ORDER.indexOf(a.name);
        const ib = EMPLOYMENT_ORDER.indexOf(b.name);
        const oa = ia === -1 ? 999 : ia;
        const ob = ib === -1 ? 999 : ib;
        return oa !== ob ? oa - ob : b.periodEnd - a.periodEnd;
      });
  })();

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.Groups sx={{ color: blueGrey[400] }} /> 従業員数 推移・増減
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#64748b' }}>
          {displayYear}年{currentMonth}月度 時点 / {fiscalYear}年度
        </Typography>
      </Box>

      {/* ⏳ 従業員データの読み込み */}
      {staffLoading && <LoadingView message="従業員データ取得中..." height="40vh" size={50} />}

      {!staffLoading && staffRows.length === 0 && (
        <Box sx={{ p: 3, border: '2px dashed #cbd5e1', borderRadius: '12px' }}>
          <Typography color="textSecondary" sx={{ fontSize: '14px' }}>
            従業員データが取得できませんでした。
          </Typography>
        </Box>
      )}

      {!staffLoading && staffRows.length > 0 && (
        <>
          {/* 📊 当月サマリーカード (全社) */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}>
            {summaryCards.map((c, idx) => {
              const display = c.signed ? `${c.value >= 0 ? '+' : ''}${c.value.toLocaleString()}` : c.value.toLocaleString();
              const valColor = c.signed ? (c.value > 0 ? '#059669' : c.value < 0 ? '#ef4444' : '#1e293b') : '#1e293b';
              return (
                <Card key={idx} sx={{ borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' }}>
                  <CardContent sx={{ px: 1.75, py: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{ p: 0.7, borderRadius: '8px', backgroundColor: c.color, color: c.iconColor, display: 'flex' }}>
                        {c.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', lineHeight: 1.3, mb: 0.25 }}>
                          {c.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '22px', fontWeight: 800, color: valColor, lineHeight: 1.2 }}>
                            {display}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
                            {c.unit}
                          </Typography>
                        </Box>
                      </Box>
                      {c.title === '期末在籍数' && periodEndYoy !== null && (
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 0.2, px: 0.7, py: 0.2, borderRadius: '4px',
                          backgroundColor: periodEndYoy >= 100 ? '#ecfdf5' : '#fef2f2',
                          color: periodEndYoy >= 100 ? '#059669' : '#ef4444',
                        }}>
                          {periodEndYoy >= 100 ? <Icons.TrendingUp sx={{ fontSize: '13px' }} /> : <Icons.TrendingDown sx={{ fontSize: '13px' }} />}
                          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                            {periodEndYoy.toFixed(1)}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* 🧩 雇用形態別 内訳 (期末在籍・通期増減を雇用形態で分解) */}
          {employmentBreakdown.length > 0 && (
            <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', mb: 3 }}>
              <Box sx={{ mb: 1.25 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
                  雇用形態別 内訳
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>
                  期末在籍（8月末時点）と通期の入社・退職・純増減を雇用形態で分解
                </Typography>
              </Box>

              {/* 期末在籍の構成バー + 凡例 */}
              {periodEndHeadcount > 0 && (
                <>
                  <Box sx={{ display: 'flex', width: '100%', height: 12, borderRadius: '6px', overflow: 'hidden', mb: 0.75, backgroundColor: '#f1f5f9' }}>
                    {employmentBreakdown.filter(e => e.periodEnd > 0).map(e => (
                      <Box
                        key={e.name}
                        title={`${e.name} ${e.periodEnd}名`}
                        sx={{ width: `${(e.periodEnd / periodEndHeadcount) * 100}%`, backgroundColor: employmentColor(e.name) }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                    {employmentBreakdown.map(e => (
                      <Box key={e.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: employmentColor(e.name) }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{e.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {/* 雇用形態別 数値テーブル (期末在籍・通期入社・通期退職・純増減・構成比) */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>雇用形態</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>期末在籍</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>通期入社</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>通期退職</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>純増減</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>構成比</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employmentBreakdown.map(e => (
                      <TableRow key={e.name} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                        <TableCell sx={{ fontSize: '13px', color: '#1e293b', fontWeight: 600, borderBottom: '1px dashed #f1f5f9' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: employmentColor(e.name) }} />
                            {e.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', borderBottom: '1px dashed #f1f5f9' }}>
                          {e.periodEnd.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: e.hires > 0 ? '#059669' : '#cbd5e1', borderBottom: '1px dashed #f1f5f9' }}>
                          {e.hires > 0 ? `+${e.hires}` : '0'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: e.leaves > 0 ? '#ef4444' : '#cbd5e1', borderBottom: '1px dashed #f1f5f9' }}>
                          {e.leaves > 0 ? `-${e.leaves}` : '0'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: e.net > 0 ? '#059669' : e.net < 0 ? '#ef4444' : '#94a3b8', borderBottom: '1px dashed #f1f5f9' }}>
                          {`${e.net >= 0 ? '+' : ''}${e.net}`}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 500, color: '#64748b', borderBottom: '1px dashed #f1f5f9' }}>
                          {periodEndHeadcount > 0 ? `${((e.periodEnd / periodEndHeadcount) * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* 合計行 (全社サマリーカードと一致) */}
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontSize: '13px', fontWeight: 800, color: '#1e293b', borderBottom: 'none' }}>合計</TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 800, color: '#1e293b', borderBottom: 'none' }}>{periodEndHeadcount.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: companyYearHires > 0 ? '#059669' : '#94a3b8', borderBottom: 'none' }}>{companyYearHires > 0 ? `+${companyYearHires}` : '0'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: companyYearLeaves > 0 ? '#ef4444' : '#94a3b8', borderBottom: 'none' }}>{companyYearLeaves > 0 ? `-${companyYearLeaves}` : '0'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 800, color: companyYearNet > 0 ? '#059669' : companyYearNet < 0 ? '#ef4444' : '#94a3b8', borderBottom: 'none' }}>{`${companyYearNet >= 0 ? '+' : ''}${companyYearNet}`}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', borderBottom: 'none' }}>100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* 📈 12ヶ月推移グラフ (全社合計) */}
          <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', mb: 3 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
                12ヶ月推移（在籍数・増減）
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>
                棒グラフ 在籍数（各月末）/ 折れ線 前年在籍・純増減
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={staffChartData} margin={{ top: 6, right: 20, left: 10, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  width={70}
                  label={{ value: '在籍数 (名)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  width={60}
                  label={{ value: '純増減 (名)', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#64748b' } }}
                />
                <Tooltip content={<StaffChartTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} />
                <Legend wrapperStyle={{ fontSize: '12px', paddingBottom: '8px' }} iconType="square" verticalAlign="top" align="right" />
                <Bar yAxisId="left" dataKey="headcount" name="在籍数" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={28}>
                  {staffChartData.map((entry, idx) => (
                    <Cell key={`cell-hc-${idx}`} fill={entry.m === currentMonth ? '#1d4ed8' : '#3b82f6'} />
                  ))}
                </Bar>
                <Line yAxisId="left" type="monotone" dataKey="prev" name="前年在籍" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#94a3b8', r: 3 }} activeDot={{ r: 5 }} />
                <Line yAxisId="right" type="monotone" dataKey="net" name="純増減" stroke="#8b5cf6" strokeWidth={2.5} dot={{ fill: '#8b5cf6', r: 4 }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          {/* 📋 月次在籍数・増減推移 (拠点絞り込みが効いていれば対象拠点合計、未指定なら全社合計) */}
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
            月次在籍数・増減推移（各月末時点・{selectedBranchCodes.length > 0 ? '対象拠点合計' : '全社合計'}）
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', overflow: 'auto', maxHeight: '65vh', mb: 3 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '160px', position: 'sticky', left: 0, zIndex: 11 }}>
                    指標
                  </TableCell>
                  {MONTH_ORDER.map(m => (
                    <TableCell key={m} align="right" sx={{ backgroundColor: m === currentMonth ? '#dbeafe' : '#f5f5f5', color: m === currentMonth ? '#1d4ed8' : 'grey.500', fontWeight: m === currentMonth ? 800 : 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '64px' }}>
                      {m}月
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* 全社合計ブロック (バナー行はテーブルタイトルと冗長なため非表示。指標行のみ描画) */}
                {renderEntityBlock('全社合計', staffRows, { isCompany: true, hideBanner: true })}

                {/* 採用計画 予実対比 (計画在籍 / 在籍差異 / 計画採用 / 採用差異)。差異 = 実績 − 計画 */}
                <TableRow sx={{ backgroundColor: '#eef2ff' }}>
                  <TableCell sx={{ fontWeight: 800, color: '#3730a3', fontSize: '0.8125rem', whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#eef2ff', borderTop: '2px solid #c7d2fe', borderBottom: '1px solid #e0e7ff' }}>
                    採用計画 予実{planVersionName ? `（${planVersionName}）` : ''}{!hasPlan && '（計画未入力）'}
                  </TableCell>
                  {MONTH_ORDER.map(m => (
                    <TableCell key={m} sx={{ backgroundColor: m === currentMonth ? '#dbeafe' : '#eef2ff', borderTop: '2px solid #c7d2fe', borderBottom: '1px solid #e0e7ff' }} />
                  ))}
                </TableRow>
                {planRowDefs.map(def => (
                  <TableRow key={def.key}>
                    <TableCell sx={{ fontSize: '13px', color: '#64748b', pl: 5, position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#ffffff', borderBottom: '1px dashed #f1f5f9' }}>
                      {def.label}
                    </TableCell>
                    {planComparison.map(p => (
                      <TableCell
                        key={p.m}
                        align="right"
                        sx={{
                          fontSize: '13px',
                          fontWeight: def.bold ? 700 : 500,
                          color: def.color(p),
                          backgroundColor: p.m === currentMonth ? '#eff6ff' : 'transparent',
                          borderBottom: '1px dashed #f1f5f9',
                        }}
                      >
                        {def.format(p)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
